import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, RotateCcw, Volume2, VolumeX, BookOpen, Heart, AlertTriangle, X, Info, Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";

// Mode normal: 6 secondes inspire/expire
const NORMAL_BREATH_DURATION = 6;

// PANIC MODE: 4 phases progressives sur 6 minutes (360 secondes)
// Basé sur la recherche clinique pour crises d'angoisse
const PANIC_PHASES = [
  {
    name: "Interruption",
    description: "Casser le cycle de panique",
    duration: 60, // 1 minute
    inhale: 4,
    hold1: 7,
    exhale: 8,
    hold2: 0,
    color: "#F1948A"
  },
  {
    name: "Stabilisation",
    description: "Retrouver un rythme",
    duration: 120, // 2 minutes
    inhale: 4,
    hold1: 4,
    exhale: 4,
    hold2: 4,
    color: "#FAD7A1"
  },
  {
    name: "Relaxation",
    description: "Respiration profonde",
    duration: 120, // 2 minutes
    inhale: 4,
    hold1: 0,
    exhale: 5,
    hold2: 0,
    color: "#AED6F1"
  },
  {
    name: "Calme",
    description: "6 respirations par minute",
    duration: 60, // 1 minute
    inhale: 4,
    hold1: 0,
    exhale: 6,
    hold2: 0,
    color: "#A3E4D7"
  }
];

const PANIC_TOTAL_TIME = 360; // 6 minutes

// Citations stoïciennes authentiques - Marc-Aurèle (Pensées pour moi-même) et Épictète (Manuel, Entretiens)
const STOIC_QUOTES = [
  // Marc-Aurèle - Pensées pour moi-même
  { text: "Tu as du pouvoir sur ton esprit, pas sur les événements extérieurs. Réalise cela, et tu trouveras la force.", author: "Marc-Aurèle, Pensées IV.3" },
  { text: "Le bonheur de ta vie dépend de la qualité de tes pensées.", author: "Marc-Aurèle, Pensées V.16" },
  { text: "Accepte les choses auxquelles le destin te lie, et aime les personnes avec qui le destin t'unit, mais aime-les vraiment.", author: "Marc-Aurèle, Pensées VI.39" },
  { text: "La meilleure vengeance est de ne pas ressembler à ton ennemi.", author: "Marc-Aurèle, Pensées VI.6" },
  { text: "Très peu de choses sont nécessaires pour vivre heureux ; et tout cela est en toi-même, dans ta façon de penser.", author: "Marc-Aurèle, Pensées VII.67" },
  { text: "Ne te laisse pas troubler par l'imagination de ta vie entière. Ne songe pas à toutes les épreuves qui pourraient t'arriver.", author: "Marc-Aurèle, Pensées VIII.36" },
  { text: "Quand tu te lèves le matin, songe quel précieux privilège c'est d'être en vie, de respirer, de penser, de jouir, d'aimer.", author: "Marc-Aurèle, Pensées II.1" },
  { text: "Si tu es affligé par quelque chose d'extérieur, ce n'est pas cette chose qui te trouble, mais le jugement que tu portes sur elle.", author: "Marc-Aurèle, Pensées VIII.47" },
  { text: "Ne gaspille plus le reste de ta vie en pensées sur autrui.", author: "Marc-Aurèle, Pensées III.4" },
  { text: "L'obstacle sur le chemin devient le chemin. Ce qui fait obstacle fait avancer.", author: "Marc-Aurèle, Pensées V.20" },
  { text: "Le temps est comme un fleuve d'événements passagers : à peine une chose apparaît-elle qu'elle est déjà emportée.", author: "Marc-Aurèle, Pensées IV.43" },
  { text: "Chaque jour, pense que c'est peut-être le dernier. L'heure que tu n'attendais pas te sera un don.", author: "Marc-Aurèle, Pensées VII.69" },
  { text: "Regarde au-dedans de toi. C'est là qu'est la source du bien, une source qui peut toujours jaillir si tu creuses toujours.", author: "Marc-Aurèle, Pensées VII.59" },
  { text: "Retire-toi en toi-même. La raison qui te gouverne a sa nature propre de se suffire à elle-même.", author: "Marc-Aurèle, Pensées VII.28" },
  { text: "Concentre-toi sur ce seul instant présent. Sépare l'avenir du passé.", author: "Marc-Aurèle, Pensées XII.3" },
  
  // Épictète - Manuel et Entretiens
  { text: "Ce qui trouble les hommes, ce ne sont pas les choses, mais les jugements qu'ils portent sur les choses.", author: "Épictète, Manuel 5" },
  { text: "Parmi les choses qui existent, certaines dépendent de nous, d'autres non.", author: "Épictète, Manuel 1" },
  { text: "Ne demande pas que les événements arrivent comme tu le veux, mais veuille-les comme ils arrivent, et tu seras heureux.", author: "Épictète, Manuel 8" },
  { text: "La vraie richesse est de n'avoir besoin de rien.", author: "Épictète, Entretiens IV.1" },
  { text: "Nul ne peut te faire du mal sans ton propre consentement.", author: "Épictète, Manuel 30" },
  { text: "Ce n'est pas ce qui t'arrive qui importe, mais comment tu y réagis.", author: "Épictète, Entretiens I.1" },
  { text: "Souviens-toi que tu es acteur d'un drame que l'auteur veut tel qu'il est.", author: "Épictète, Manuel 17" },
  { text: "Si tu veux être libre, ne désire rien et ne fuis rien de ce qui dépend des autres.", author: "Épictète, Manuel 14" },
  { text: "Les difficultés révèlent les hommes. Quand une difficulté t'assaille, souviens-toi que c'est pour toi l'occasion de montrer ta force.", author: "Épictète, Entretiens I.24" },
  { text: "Ne dis jamais de rien : je l'ai perdu. Dis plutôt : je l'ai rendu.", author: "Épictète, Manuel 11" },
  { text: "Si tu veux progresser, accepte de paraître ignorant sur bien des sujets.", author: "Épictète, Manuel 13" },
  { text: "Commence par les petites choses. Ta soupe est froide ? Ne te mets pas en colère. C'est un entraînement pour les grandes épreuves.", author: "Épictète, Manuel 12" },
  { text: "Garde ton âme calme et sans passion devant ce qui vient du dehors.", author: "Épictète, Manuel 2" },
  
  // Sénèque - Lettres à Lucilius (bonus)
  { text: "Ce n'est pas parce que les choses sont difficiles que nous n'osons pas, c'est parce que nous n'osons pas qu'elles sont difficiles.", author: "Sénèque, Lettres 104" },
  { text: "La vie, ce n'est pas attendre que l'orage passe, c'est apprendre à danser sous la pluie.", author: "Sénèque" },
  { text: "Nous souffrons plus souvent en imagination qu'en réalité.", author: "Sénèque, Lettres 13" },
  { text: "Il n'y a pas de vent favorable pour celui qui ne sait pas où il va.", author: "Sénèque, Lettres 71" },
  { text: "Chaque nuit, avant de dormir, interroge-toi : quel défaut ai-je combattu aujourd'hui ?", author: "Sénèque, De la colère III.36" },
];

export default function DashboardPage() {
  const { authHeaders } = useAuth();
  const [isActive, setIsActive] = useState(false);
  const [isPanicMode, setIsPanicMode] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle, inhale, hold1, exhale, hold2
  const [countdown, setCountdown] = useState(NORMAL_BREATH_DURATION);
  const [cycleCount, setCycleCount] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showStoicQuote, setShowStoicQuote] = useState(false);
  const [currentStoicQuote, setCurrentStoicQuote] = useState(null);
  const [showPanicMenu, setShowPanicMenu] = useState(false);
  const [showPanicInfo, setShowPanicInfo] = useState(false);
  const [currentPanicPhase, setCurrentPanicPhase] = useState(0);
  const [historyData, setHistoryData] = useState([]);
  const [calendarMonth, setCalendarMonth] = useState(new Date()); // Current displayed month
  const [showCalendar, setShowCalendar] = useState(false); // Calendar modal state
  
  const timerRef = useRef(null);
  const countdownRef = useRef(null);
  const startTimeRef = useRef(null);
  const isActiveRef = useRef(false);
  const isPanicRef = useRef(false);
  const audioContextRef = useRef(null);
  const wakeLockRef = useRef(null);
  const panicPhaseRef = useRef(0);

  // Wake Lock API
  const requestWakeLock = async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
      }
    } catch (err) {
      console.log('Wake Lock not supported');
    }
  };

  const releaseWakeLock = () => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release();
      wakeLockRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      releaseWakeLock();
    };
  }, []);

  useEffect(() => {
    isActiveRef.current = isActive;
    isPanicRef.current = isPanicMode;
    
    if (isActive) {
      requestWakeLock();
    } else {
      releaseWakeLock();
    }
  }, [isActive, isPanicMode]);

  // Sound functions
  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  // Son doux original
  const playSound = useCallback(() => {
    if (!soundEnabled) return;
    
    try {
      if (audioContextRef.current?.state === 'suspended') {
        audioContextRef.current.resume();
      }
      
      const ctx = getAudioContext();
      const now = ctx.currentTime;
      
      const osc = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      osc.connect(gainNode);
      gainNode.connect(ctx.destination);
      osc.frequency.setValueAtTime(392, now); // G4
      osc.type = 'sine';
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(0.08, now + 0.1);
      gainNode.gain.exponentialRampToValueAtTime(0.001, now + 1.2);
      
      osc.start(now);
      osc.stop(now + 1.2);
    } catch (e) {
      console.log("Audio error");
    }
  }, [soundEnabled, getAudioContext]);

  // Fetch breathing history
  const fetchHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/breathing/history`, authHeaders());
      setHistoryData(response.data || []);
    } catch (e) {
      console.error("Error fetching history:", e);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Get current panic phase based on elapsed time
  const getCurrentPanicPhase = useCallback((elapsed) => {
    let accumulatedTime = 0;
    for (let i = 0; i < PANIC_PHASES.length; i++) {
      accumulatedTime += PANIC_PHASES[i].duration;
      if (elapsed < accumulatedTime) {
        return i;
      }
    }
    return PANIC_PHASES.length - 1;
  }, []);

  // Normal breathing cycle (6s in, 6s out)
  const runNormalCycle = useCallback(() => {
    if (!isActiveRef.current) return;
    
    setPhase("inhale");
    setCountdown(NORMAL_BREATH_DURATION);
    playSound();
    
    let count = NORMAL_BREATH_DURATION;
    
    countdownRef.current = setInterval(() => {
      count--;
      if (count > 0) {
        setCountdown(count);
      } else {
        clearInterval(countdownRef.current);
        if (!isActiveRef.current) return;
        
        setPhase("exhale");
        setCountdown(NORMAL_BREATH_DURATION);
        playSound();
        
        count = NORMAL_BREATH_DURATION;
        
        countdownRef.current = setInterval(() => {
          count--;
          if (count > 0) {
            setCountdown(count);
          } else {
            clearInterval(countdownRef.current);
            if (!isActiveRef.current) return;
            
            setCycleCount(prev => prev + 1);
            
            if (isActiveRef.current) {
              runNormalCycle();
            }
          }
        }, 1000);
      }
    }, 1000);
  }, [playSound]);

  // Panic mode with evolving phases
  const runPanicCycle = useCallback(() => {
    if (!isActiveRef.current) return;
    
    const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
    const phaseIndex = getCurrentPanicPhase(elapsed);
    const currentPhaseConfig = PANIC_PHASES[phaseIndex];
    
    panicPhaseRef.current = phaseIndex;
    setCurrentPanicPhase(phaseIndex);
    
    // Check if 6 minutes reached
    if (elapsed >= PANIC_TOTAL_TIME) {
      handleStop();
      return;
    }
    
    const runPhaseStep = (stepPhase, duration, nextStep) => {
      if (!isActiveRef.current) return;
      if (duration === 0) {
        nextStep();
        return;
      }
      
      setPhase(stepPhase);
      setCountdown(duration);
      if (stepPhase === "inhale" || stepPhase === "exhale") {
        playSound();
      }
      
      let count = duration;
      
      countdownRef.current = setInterval(() => {
        count--;
        if (count > 0) {
          setCountdown(count);
        } else {
          clearInterval(countdownRef.current);
          if (!isActiveRef.current) return;
          nextStep();
        }
      }, 1000);
    };
    
    // Phase 1: Inhale
    runPhaseStep("inhale", currentPhaseConfig.inhale, () => {
      // Phase 2: Hold (if any)
      runPhaseStep("hold1", currentPhaseConfig.hold1, () => {
        // Phase 3: Exhale
        runPhaseStep("exhale", currentPhaseConfig.exhale, () => {
          // Phase 4: Hold (if any)
          runPhaseStep("hold2", currentPhaseConfig.hold2, () => {
            setCycleCount(prev => prev + 1);
            
            // Check elapsed time again and continue
            const newElapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
            if (newElapsed >= PANIC_TOTAL_TIME) {
              handleStop();
            } else if (isActiveRef.current) {
              runPanicCycle();
            }
          });
        });
      });
    });
  }, [playSound, getCurrentPanicPhase]);

  useEffect(() => {
    if (isActive) {
      startTimeRef.current = Date.now();
      
      if (isPanicMode) {
        setCurrentPanicPhase(0);
        panicPhaseRef.current = 0;
        runPanicCycle();
      } else {
        runNormalCycle();
      }
      
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setTotalTime(elapsed);
        
        // Update panic phase
        if (isPanicRef.current) {
          const newPhase = getCurrentPanicPhase(elapsed);
          if (newPhase !== panicPhaseRef.current) {
            setCurrentPanicPhase(newPhase);
            panicPhaseRef.current = newPhase;
          }
        }
      }, 1000);
    }

    return () => {
      clearInterval(timerRef.current);
      clearInterval(countdownRef.current);
    };
  }, [isActive, isPanicMode, runNormalCycle, runPanicCycle, getCurrentPanicPhase]);

  const handleStart = (panicMode = false) => {
    if (audioContextRef.current?.state === 'suspended') {
      audioContextRef.current.resume();
    }
    setIsPanicMode(panicMode);
    setIsActive(true);
    setCycleCount(0);
    setTotalTime(0);
    setShowStoicQuote(false);
    setShowPanicMenu(false);
    setCurrentPanicPhase(0);
  };

  const handleStop = async () => {
    setIsActive(false);
    isActiveRef.current = false;
    setPhase("idle");
    clearInterval(countdownRef.current);
    clearInterval(timerRef.current);
    
    if (cycleCount > 0) {
      try {
        await axios.post(`${API}/breathing/session`, {
          date: new Date().toISOString().split('T')[0],
          duration_seconds: totalTime,
          cycles_completed: cycleCount
        }, authHeaders());
        
        const randomQuote = STOIC_QUOTES[Math.floor(Math.random() * STOIC_QUOTES.length)];
        setCurrentStoicQuote(randomQuote);
        setShowStoicQuote(true);
      } catch (e) {
        console.error(e);
      }
    }
    
    setIsPanicMode(false);
  };

  const handleReset = () => {
    setIsActive(false);
    isActiveRef.current = false;
    setPhase("idle");
    setCycleCount(0);
    setTotalTime(0);
    setShowStoicQuote(false);
    setIsPanicMode(false);
    setCurrentPanicPhase(0);
    clearInterval(countdownRef.current);
    clearInterval(timerRef.current);
  };

  const handleCloseQuote = () => {
    setShowStoicQuote(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getPhaseText = () => {
    switch (phase) {
      case "inhale": return "Inspire...";
      case "hold1": return "Retiens...";
      case "exhale": return "Expire...";
      case "hold2": return "Retiens...";
      default: return "Respire";
    }
  };

  const getCircleColor = () => {
    if (isPanicMode && isActive) {
      return PANIC_PHASES[currentPanicPhase]?.color || "#D4C4E0";
    }
    switch (phase) {
      case "inhale": return "#E8B4BC"; // Rose Quartz
      case "exhale": return "#D4C4E0"; // Lavender
      default: return "#F5D6D6"; // Blush
    }
  };

  const getCircleScale = () => {
    switch (phase) {
      case "inhale": return 1.6;
      case "hold1": return 1.6;
      case "exhale": return 1;
      case "hold2": return 1;
      default: return 1.25;
    }
  };

  const getCurrentPanicPhaseConfig = () => {
    return PANIC_PHASES[currentPanicPhase] || PANIC_PHASES[0];
  };

  const remainingPanicTime = isPanicMode ? Math.max(0, PANIC_TOTAL_TIME - totalTime) : 0;

  return (
    <div className="min-h-[90vh] flex flex-col" data-testid="dashboard-page">
      {/* Stoic Quote Modal */}
      <AnimatePresence>
        {showStoicQuote && currentStoicQuote && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#4A3F55]/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={handleCloseQuote}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl p-8 max-w-md w-full shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-center mb-6">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4C4E0] to-[#E8B4BC] flex items-center justify-center">
                  <BookOpen className="text-white" size={28} />
                </div>
              </div>
              
              <p className="text-center text-lg text-[#4A3F55] italic leading-relaxed mb-4">
                "{currentStoicQuote.text}"
              </p>
              <p className="text-center text-[#9A8F97] font-medium mb-6">
                — {currentStoicQuote.author}
              </p>
              
              <div className="text-center mb-6">
                <p className="text-sm text-[#E8B4BC] font-medium">
                  {cycleCount} cycles · {formatTime(totalTime)}
                </p>
                <p className="text-xs text-[#9A8F97]">
                  Bravo, tu as pris soin de toi
                </p>
              </div>
              
              <Button
                onClick={handleCloseQuote}
                className="w-full h-12 rounded-full bg-gradient-to-r from-[#E8B4BC] to-[#D4C4E0] hover:opacity-90 text-white font-semibold"
              >
                Fermer
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Panic Mode Info Modal */}
      <AnimatePresence>
        {showPanicInfo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#4A3F55]/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowPanicInfo(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl p-6 max-w-md w-full shadow-xl max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#D4C4E0] flex items-center justify-center">
                    <Info className="text-[#4A3F55]" size={20} />
                  </div>
                  <h3 className="font-nunito font-bold text-[#4A3F55] text-lg">Pourquoi ce protocole ?</h3>
                </div>
                <button onClick={() => setShowPanicInfo(false)} className="text-[#9A8F97] p-1">
                  <X size={20} />
                </button>
              </div>
              
              <p className="text-sm text-[#9A8F97] mb-4">
                Ce cycle de 6 minutes est basé sur la recherche clinique en gestion des crises d'angoisse. 
                Il évolue progressivement pour accompagner ton système nerveux vers le calme.
              </p>
              
              <div className="space-y-3">
                {PANIC_PHASES.map((p, index) => (
                  <div 
                    key={index}
                    className="p-3 rounded-xl border-2"
                    style={{ borderColor: p.color, backgroundColor: `${p.color}15` }}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-[#4A3F55] text-sm">
                        Phase {index + 1}: {p.name}
                      </span>
                      <span className="text-xs text-[#9A8F97]">
                        {p.duration / 60} min
                      </span>
                    </div>
                    <p className="text-xs text-[#9A8F97] mb-2">{p.description}</p>
                    <div className="flex gap-1 flex-wrap">
                      <span className="text-xs px-2 py-0.5 bg-white rounded-full text-[#4A3F55]">
                        {p.inhale}s inspire
                      </span>
                      {p.hold1 > 0 && (
                        <span className="text-xs px-2 py-0.5 bg-white rounded-full text-[#4A3F55]">
                          {p.hold1}s retenir
                        </span>
                      )}
                      <span className="text-xs px-2 py-0.5 bg-white rounded-full text-[#4A3F55]">
                        {p.exhale}s expire
                      </span>
                      {p.hold2 > 0 && (
                        <span className="text-xs px-2 py-0.5 bg-white rounded-full text-[#4A3F55]">
                          {p.hold2}s retenir
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 p-3 bg-[#FDF8F8] rounded-xl">
                <p className="text-xs text-[#9A8F97] leading-relaxed">
                  <strong>Sources :</strong> Technique 4-7-8 (Dr. Andrew Weil), Box Breathing (Navy SEALs), 
                  6 respirations/minute (fréquence de résonance du système nerveux parasympathique).
                </p>
              </div>
              
              <Button
                onClick={() => setShowPanicInfo(false)}
                className="w-full h-10 mt-4 rounded-full bg-[#4A3F55] hover:opacity-90 text-white font-medium"
              >
                Compris
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Panic Mode Menu */}
      <AnimatePresence>
        {showPanicMenu && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#4A3F55]/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowPanicMenu(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4A5A5] to-[#C9A8C9] flex items-center justify-center">
                    <AlertTriangle className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-nunito font-bold text-[#4A3F55] text-lg">PANIC MODE</h3>
                    <p className="text-xs text-[#9A8F97]">Crise d'angoisse</p>
                  </div>
                </div>
                <button onClick={() => setShowPanicMenu(false)} className="text-[#9A8F97]">
                  <X size={20} />
                </button>
              </div>
              
              <div className="bg-[#F5D6D6]/30 rounded-2xl p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-[#4A3F55] font-medium">Protocole progressif · 6 minutes</p>
                  <button 
                    onClick={() => { setShowPanicMenu(false); setShowPanicInfo(true); }}
                    className="w-6 h-6 rounded-full bg-[#D4C4E0] flex items-center justify-center"
                  >
                    <Info size={14} className="text-[#4A3F55]" />
                  </button>
                </div>
                <p className="text-xs text-[#9A8F97] leading-relaxed mb-3">
                  4 phases qui évoluent pour calmer progressivement ton système nerveux.
                </p>
                <div className="flex gap-1">
                  {PANIC_PHASES.map((p, i) => (
                    <div 
                      key={i} 
                      className="flex-1 h-2 rounded-full" 
                      style={{ backgroundColor: p.color }}
                      title={p.name}
                    />
                  ))}
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-[10px] text-[#9A8F97]">Interruption</span>
                  <span className="text-[10px] text-[#9A8F97]">Calme</span>
                </div>
              </div>
              
              <Button
                onClick={() => handleStart(true)}
                className="w-full h-12 rounded-full bg-gradient-to-r from-[#D4A5A5] to-[#C9A8C9] hover:opacity-90 text-white font-semibold"
              >
                Commencer le cycle de 6 min
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Breathing Section */}
      <div className="flex-1 flex flex-col items-center justify-center py-4">
        {/* Sound controls */}
        {/* Sound control */}
        <div className="absolute top-4 left-4">
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            data-testid="sound-toggle-button"
            className={`p-2 rounded-full transition-all ${
              soundEnabled ? "bg-[#E8B4BC]/20 text-[#4A3F55]" : "bg-[#FDF8F8] text-[#9A8F97]"
            }`}
            title={soundEnabled ? "Couper le son" : "Activer le son"}
          >
            {soundEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
          </button>
        </div>

        {/* Panic Mode indicator */}
        {isPanicMode && isActive && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 px-4 py-2 rounded-full"
            style={{ backgroundColor: `${getCurrentPanicPhaseConfig().color}30` }}
          >
            <p className="text-sm font-medium text-[#4A3F55]">
              Phase {currentPanicPhase + 1}: {getCurrentPanicPhaseConfig().name} · {formatTime(remainingPanicTime)} restantes
            </p>
          </motion.div>
        )}

        {/* Breathing Circle Container */}
        <div className="relative flex items-center justify-center" style={{ height: '280px', width: '280px' }}>
          <motion.div
            animate={{
              scale: getCircleScale(),
              backgroundColor: getCircleColor()
            }}
            transition={{
              duration: phase === "idle" ? 0.5 : (isPanicMode ? getCurrentPanicPhaseConfig().inhale : NORMAL_BREATH_DURATION),
              ease: "easeInOut"
            }}
            className="w-44 h-44 rounded-full flex items-center justify-center shadow-mint absolute"
            style={{ transformOrigin: 'center center' }}
            data-testid="breathing-circle"
          >
            <div className="text-center">
              <p className="text-[#4A3F55] font-nunito font-medium text-lg">
                {getPhaseText()}
              </p>
              {phase !== "idle" && (
                <p className="text-[#4A3F55]/60 font-nunito font-light text-3xl mt-1">
                  {countdown}
                </p>
              )}
            </div>
          </motion.div>
        </div>

        {/* Timer and cycles */}
        <div className="mt-8 flex items-center gap-8 text-center">
          <div>
            <p className="text-3xl font-bold text-[#4A3F55]">
              {formatTime(totalTime)}
            </p>
            <p className="text-[#9A8F97] text-sm">Durée</p>
          </div>
          <div className="w-px h-12 bg-[#E8B4BC]/30" />
          <div>
            <p className="text-3xl font-bold text-[#4A3F55]">
              {cycleCount}
            </p>
            <p className="text-[#9A8F97] text-sm">Cycles</p>
          </div>
        </div>

        {/* Panic Phase Progress */}
        {isPanicMode && isActive && (
          <div className="mt-4 flex gap-1 w-48">
            {PANIC_PHASES.map((p, i) => (
              <div 
                key={i} 
                className={`flex-1 h-2 rounded-full transition-all ${
                  i <= currentPanicPhase ? 'opacity-100' : 'opacity-30'
                }`}
                style={{ backgroundColor: p.color }}
              />
            ))}
          </div>
        )}

        {/* Controls */}
        <div className="mt-8 flex flex-col items-center gap-4">
          {!isActive ? (
            <div className="flex gap-4">
              <Button
                onClick={() => handleStart(false)}
                data-testid="breathing-start-button"
                className="h-14 px-10 rounded-full bg-gradient-to-r from-[#E8B4BC] to-[#D4C4E0] hover:opacity-90 text-white font-semibold text-lg shadow-soft flex items-center gap-2"
              >
                <Heart size={18} className="fill-white" />
                <Play size={24} />
                Commencer
                <Heart size={18} className="fill-white" />
              </Button>
            </div>
          ) : (
            <Button
              onClick={handleStop}
              data-testid="breathing-stop-button"
              className={`h-14 px-10 rounded-full hover:opacity-90 font-semibold text-lg flex items-center gap-2 ${
                isPanicMode 
                  ? "bg-gradient-to-r from-[#D4A5A5] to-[#C9A8C9] text-white"
                  : "bg-gradient-to-r from-[#D4C4E0] to-[#E8B4BC] text-white"
              }`}
            >
              <Heart size={18} className="fill-white" />
              <Pause size={24} />
              Arrêter
              <Heart size={18} className="fill-white" />
            </Button>
          )}
          
          {!isActive && (
            <Button
              onClick={() => setShowPanicMenu(true)}
              data-testid="panic-mode-button"
              variant="outline"
              className="h-12 px-6 rounded-full border-2 border-[#D4A5A5] text-[#D4A5A5] hover:bg-[#D4A5A5]/10 font-medium flex items-center gap-2"
            >
              <Heart size={14} className="fill-[#D4A5A5] text-[#D4A5A5]" />
              <AlertTriangle size={18} />
              PANIC MODE
              <Heart size={14} className="fill-[#D4A5A5] text-[#D4A5A5]" />
            </Button>
          )}
          
          {(cycleCount > 0 || totalTime > 0) && !isActive && (
            <Button
              onClick={handleReset}
              data-testid="breathing-reset-button"
              variant="ghost"
              className="text-[#9A8F97] hover:text-[#4A3F55] flex items-center gap-1"
            >
              <Heart size={12} className="fill-[#D4C4E0] text-[#D4C4E0]" />
              <RotateCcw size={18} />
              Réinitialiser
              <Heart size={12} className="fill-[#D4C4E0] text-[#D4C4E0]" />
            </Button>
          )}
        </div>
      </div>

      {/* Bottom section with quote */}
      <div className="text-center py-4 space-y-3 px-4">
        <p className="text-[#9A8F97] text-xs flex items-center justify-center gap-2">
          <Heart size={8} className="fill-[#D4C4E0] text-[#D4C4E0]" />
          {isPanicMode && isActive 
            ? `${getCurrentPanicPhaseConfig().inhale}s inspire${getCurrentPanicPhaseConfig().hold1 > 0 ? ` · ${getCurrentPanicPhaseConfig().hold1}s retenir` : ''} · ${getCurrentPanicPhaseConfig().exhale}s expire${getCurrentPanicPhaseConfig().hold2 > 0 ? ` · ${getCurrentPanicPhaseConfig().hold2}s retenir` : ''}`
            : "6 secondes inspire · 6 secondes expire"
          }
          <Heart size={8} className="fill-[#D4C4E0] text-[#D4C4E0]" />
        </p>
        
        <p className="text-[#4A3F55]/70 text-xs italic leading-relaxed max-w-md mx-auto">
          "Que la force me soit donnée de supporter ce qui ne peut être changé,
          le courage de changer ce qui peut l'être,
          et la sagesse de distinguer l'un de l'autre."
        </p>
        
        <p className="text-[#E8B4BC] text-sm font-medium flex items-center justify-center gap-2">
          <Heart size={14} className="fill-[#E8B4BC]" />
          Sois forte Emma chérie, je t'aime fort.
          <Heart size={14} className="fill-[#E8B4BC]" />
        </p>
        
        {/* Calendar Button */}
        <button
          onClick={() => setShowCalendar(true)}
          className="mt-2 px-6 py-2.5 rounded-full bg-white shadow-soft hover:shadow-lg transition-all text-[#4A3F55] text-sm font-medium flex items-center justify-center gap-2 mx-auto border border-[#F5D6D6]"
        >
          <Heart size={12} className="fill-[#E8B4BC] text-[#E8B4BC]" />
          <Calendar size={16} className="text-[#D4C4E0]" />
          Mon historique
          <Heart size={12} className="fill-[#E8B4BC] text-[#E8B4BC]" />
        </button>
      </div>

      {/* Calendar Modal */}
      <AnimatePresence>
        {showCalendar && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#4A3F55]/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowCalendar(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl p-5 max-w-md w-full shadow-xl max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <div className="flex justify-end mb-2">
                <button onClick={() => setShowCalendar(false)} className="text-[#9A8F97] hover:text-[#4A3F55] p-1">
                  <X size={20} />
                </button>
              </div>
              
              {/* Month Navigation Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 flex-1 justify-center">
                  <button
                    onClick={() => {
                      const newDate = new Date(calendarMonth);
                      newDate.setMonth(newDate.getMonth() - 1);
                      setCalendarMonth(newDate);
                    }}
                    className="p-1.5 rounded-full hover:bg-[#F5D6D6]/50 transition-colors text-[#9A8F97] hover:text-[#4A3F55]"
                    title="Mois précédent"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <h3 className="font-nunito font-semibold text-[#4A3F55] text-base min-w-[160px] text-center capitalize flex items-center justify-center gap-2">
                    <Heart size={12} className="fill-[#E8B4BC] text-[#E8B4BC]" />
                    {calendarMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                    <Heart size={12} className="fill-[#E8B4BC] text-[#E8B4BC]" />
                  </h3>
                  <button
                    onClick={() => {
                      const newDate = new Date(calendarMonth);
                      newDate.setMonth(newDate.getMonth() + 1);
                      const now = new Date();
                      if (newDate.getFullYear() < now.getFullYear() || 
                          (newDate.getFullYear() === now.getFullYear() && newDate.getMonth() <= now.getMonth())) {
                        setCalendarMonth(newDate);
                      }
                    }}
                    className={`p-1.5 rounded-full transition-colors ${
                      calendarMonth.getMonth() === new Date().getMonth() && calendarMonth.getFullYear() === new Date().getFullYear()
                        ? 'text-[#D5D5D5] cursor-not-allowed'
                        : 'hover:bg-[#F5D6D6]/50 text-[#9A8F97] hover:text-[#4A3F55]'
                    }`}
                    disabled={calendarMonth.getMonth() === new Date().getMonth() && calendarMonth.getFullYear() === new Date().getFullYear()}
                    title="Mois suivant"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
              
              {/* Legend */}
              <div className="flex items-center justify-center gap-3 mb-4 text-[9px] text-[#9A8F97]">
                <div className="flex gap-1 items-center">
                  <div className="w-3 h-3 rounded-sm bg-[#FDF8F8] border border-[#E8B4BC]/20" />
                  <span>0</span>
                </div>
                <div className="flex gap-1 items-center">
                  <div className="w-3 h-3 rounded-sm bg-[#E8B4BC]/30" />
                  <span>1-5</span>
                </div>
                <div className="flex gap-1 items-center">
                  <div className="w-3 h-3 rounded-sm bg-[#E8B4BC]/60" />
                  <span>6-10</span>
                </div>
                <div className="flex gap-1 items-center">
                  <div className="w-3 h-3 rounded-sm bg-[#E8B4BC]" />
                  <span>10+</span>
                </div>
                <div className="flex gap-1 items-center">
                  <Heart size={12} className="fill-[#D4A5A5] text-[#D4A5A5]" />
                  <span>crise</span>
                </div>
              </div>
              
              {/* Full month calendar view */}
              <div className="grid grid-cols-7 gap-1.5">
                {/* Day headers */}
                {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((day, i) => (
                  <div key={`header-${i}`} className="text-center text-[10px] text-[#9A8F97] font-medium pb-1">
                    {day}
                  </div>
                ))}
                
                {(() => {
                  const today = new Date();
                  const days = [];
                  
                  const year = calendarMonth.getFullYear();
                  const month = calendarMonth.getMonth();
                  const firstDayOfMonth = new Date(year, month, 1);
                  const lastDayOfMonth = new Date(year, month + 1, 0);
                  
                  let startDayOfWeek = firstDayOfMonth.getDay();
                  startDayOfWeek = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;
                  
                  for (let i = 0; i < startDayOfWeek; i++) {
                    const prevMonthDay = new Date(year, month, -startDayOfWeek + i + 1);
                    days.push(
                      <div
                        key={`prev-${i}`}
                        className="aspect-square rounded-lg flex items-center justify-center text-[11px] font-medium text-[#D5D5D5]"
                        style={{ backgroundColor: '#FAFAFA' }}
                      >
                        {prevMonthDay.getDate()}
                      </div>
                    );
                  }
                  
                  for (let day = 1; day <= lastDayOfMonth.getDate(); day++) {
                    const date = new Date(year, month, day);
                    const dateStr = date.toISOString().split('T')[0];
                    const dayData = historyData.find(h => h.date === dateStr);
                    
                    const cycles = dayData?.total_cycles || 0;
                    const hasPanic = dayData?.panic_sessions > 0;
                    const isToday = date.toDateString() === today.toDateString();
                    const isFuture = date > today;
                    
                    let bgColor = '#FDF8F8';
                    let textColor = '#9A8F97';
                    
                    if (isFuture) {
                      bgColor = '#FAFAFA';
                      textColor = '#D5D5D5';
                    } else if (hasPanic) {
                      bgColor = '#D4A5A5';
                      textColor = '#FFFFFF';
                    } else if (cycles > 10) {
                      bgColor = '#E8B4BC';
                      textColor = '#4A3F55';
                    } else if (cycles > 5) {
                      bgColor = 'rgba(232, 180, 188, 0.6)';
                      textColor = '#4A3F55';
                    } else if (cycles > 0) {
                      bgColor = 'rgba(232, 180, 188, 0.3)';
                      textColor = '#4A3F55';
                    }
                    
                    days.push(
                      <div
                        key={dateStr}
                        className={`aspect-square rounded-lg flex items-center justify-center text-[11px] font-medium transition-all hover:scale-110 cursor-default ${
                          isToday ? 'ring-2 ring-[#D4C4E0] ring-offset-1' : ''
                        }`}
                        style={{ backgroundColor: bgColor, color: textColor }}
                        title={`${date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}${cycles > 0 ? ` - ${cycles} cycles` : ''}${hasPanic ? ' (crise)' : ''}`}
                      >
                        {day}
                      </div>
                    );
                  }
                  
                  const totalCells = days.length;
                  const remainingCells = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7);
                  for (let i = 0; i < remainingCells; i++) {
                    days.push(
                      <div
                        key={`next-${i}`}
                        className="aspect-square rounded-lg flex items-center justify-center text-[11px] font-medium text-[#D5D5D5]"
                        style={{ backgroundColor: '#FAFAFA' }}
                      >
                        {i + 1}
                      </div>
                    );
                  }
                  
                  return days;
                })()}
              </div>
              
              {/* Mini stats - for displayed month */}
              {(() => {
                const year = calendarMonth.getFullYear();
                const month = calendarMonth.getMonth();
                const monthData = historyData.filter(h => {
                  const d = new Date(h.date);
                  return d.getFullYear() === year && d.getMonth() === month;
                });
                const totalCycles = monthData.reduce((sum, h) => sum + h.total_cycles, 0);
                const totalSessions = monthData.reduce((sum, h) => sum + h.sessions, 0);
                const totalPanic = monthData.reduce((sum, h) => sum + h.panic_sessions, 0);
                
                return (
                  <div className="flex justify-center gap-8 mt-4 pt-4 border-t border-[#F5D6D6]/50">
                    <div className="text-center">
                      <p className="text-xl font-bold text-[#E8B4BC] flex items-center justify-center gap-1">
                        <Heart size={14} className="fill-[#E8B4BC] text-[#E8B4BC]" />
                        {totalCycles}
                      </p>
                      <p className="text-[10px] text-[#9A8F97]">Cycles</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold text-[#D4C4E0] flex items-center justify-center gap-1">
                        <Heart size={14} className="fill-[#D4C4E0] text-[#D4C4E0]" />
                        {totalSessions}
                      </p>
                      <p className="text-[10px] text-[#9A8F97]">Sessions</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold text-[#D4A5A5] flex items-center justify-center gap-1">
                        <Heart size={14} className="fill-[#D4A5A5] text-[#D4A5A5]" />
                        {totalPanic}
                      </p>
                      <p className="text-[10px] text-[#9A8F97]">Crises</p>
                    </div>
                  </div>
                );
              })()}
              
              {/* Close button at bottom */}
              <Button
                onClick={() => setShowCalendar(false)}
                className="w-full h-10 mt-4 rounded-full bg-gradient-to-r from-[#E8B4BC] to-[#D4C4E0] hover:opacity-90 text-white font-medium flex items-center justify-center gap-2"
              >
                <Heart size={14} className="fill-white" />
                Fermer
                <Heart size={14} className="fill-white" />
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
